import {
  ENTITY_ACCESS_RIGHTS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
} from "constants/constants";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  createConsultantQuery,
  deleteConsultantQuery,
  getConsultantByIdQuery,
  getConsultantBySlugQuery,
  updateConsultantQuery,
  getConsultantSixYearsReviewQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { withUserScopeCheck } from "utils/helper";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const ConsultantContext = createContext({});

export const useConsultantContext = () => useContext(ConsultantContext);

export const ConsultantProvider = ({ ...props }) => {
  const [consultantSixYearsReview, setConsultantSixYearsReview] = useState([]);
  const [createdConsultant, setCreatedConsultant] = useState({});
  const [consultantById, setConsultantById] = useState({});
  const [consultantAccessRights, setConsultantAccessRights] = useState([]);
  const { apiErrorResponse, setApiErrorResponse } = useUserContext();

  const [createConsultantStatus, setCreateConsultantStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [editConsultantStatus, setEditConsultantStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [deleteConsultantStatus, setDeleteConsultantStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [displayConsultantStatus, setDisplayConsultantStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [consultantBySlug, setConsultantBySlug] = useState({});
  const lastConsultantSlugRef = useRef(null);
  const { userScope } = useUserContext();

  const getConsultantBySlug = useCallback(
    async (slug) => {
      if (slug !== lastConsultantSlugRef.current) {
        setDisplayConsultantStatus(QUERY_STATUS.LOADING);
        lastConsultantSlugRef.current = slug;
      }
      setConsultantAccessRights([]);
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await getConsultantBySlugQuery({
              slug,
              userScope: userScope,
            }),
          scope: userScope,
        });
        const hasGetAccessRights = result?.access.includes(
          ENTITY_ACCESS_RIGHTS.GET
        );
        if (hasGetAccessRights) {
          setConsultantBySlug({ ...result?.contract, ...result?.consultant });
          setDisplayConsultantStatus(QUERY_STATUS.SUCCESS);
          setConsultantAccessRights(result?.access);
        }
      } catch (e) {
        console.error("getConsultantBySlug", e);
        if (e.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
          setDisplayConsultantStatus(QUERY_STATUS.NOT_FOUND);
        } else {
          setDisplayConsultantStatus(QUERY_STATUS.ERROR);
        }
        throw e;
      }
    },
    [userScope]
  );

  const consultantCreate = useCallback(
    async (data) => {
      try {
        setCreateConsultantStatus(QUERY_STATUS.LOADING);
        setApiErrorResponse(null);
        const result = await withUserScopeCheck({
          callback: async () =>
            await createConsultantQuery({
              data,
              userScope: userScope,
            }),
          scope: userScope,
        });
        if (result) {
          setCreateConsultantStatus(QUERY_STATUS.SUCCESS);
          setCreatedConsultant({ ...result.contract, ...result.consultant });
        }
      } catch (e) {
        setCreateConsultantStatus(QUERY_STATUS.ERROR);
        setApiErrorResponse(
          extractApiFieldErrorsFlat(e?.response?.data?.["bad-request"])
        );
        console.error("consultantCreate", e);
      }
    },
    [userScope]
  );

  const consultantUpdate = useCallback(
    async (id, data) => {
      try {
        setEditConsultantStatus(QUERY_STATUS.LOADING);
        setApiErrorResponse(null);
        const updatedConsultant = await withUserScopeCheck({
          callback: async () =>
            await updateConsultantQuery({
              id,
              data,
              userScope: userScope,
            }),
          scope: userScope,
        });
        setConsultantById({
          ...updatedConsultant.contract,
          ...updatedConsultant.consultant,
        });
        setEditConsultantStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setEditConsultantStatus(QUERY_STATUS.ERROR);
        setApiErrorResponse(
          extractApiFieldErrorsFlat(e?.response?.data?.["bad-request"])
        );
        console.error("consultantUpdate", e);
      }
    },
    [userScope]
  );

  const consultantByIdGetter = useCallback(
    async (id) => {
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await getConsultantByIdQuery({
              id,
              userScope: userScope,
            }),
          scope: userScope,
        });
        setConsultantById({ ...result.contract, ...result.consultant });
        setDisplayConsultantStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        console.error("consultantByIdGetter", e);
        setDisplayConsultantStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [setConsultantById, userScope]
  );

  const deleteConsultant = useCallback(
    async (id) => {
      setDeleteConsultantStatus(QUERY_STATUS.LOADING);
      try {
        await withUserScopeCheck({
          callback: async () =>
            await deleteConsultantQuery({ id, userScope: userScope }),
          scope: userScope,
        });
        setDeleteConsultantStatus(QUERY_STATUS.SUCCESS);
        setConsultantById({});
        setConsultantBySlug({});
      } catch (e) {
        setDeleteConsultantStatus(QUERY_STATUS.ERROR);
        console.error("deleteConsultant", e);
        throw e;
      }
    },
    [userScope]
  );

  const getConsultantSixYearsReview = useCallback(
    async ({ id }) => {
      try {
        const result = await getConsultantSixYearsReviewQuery({
          id,
          userScope,
        });
        setConsultantSixYearsReview(result);
      } catch (e) {
        console.error("getConsultantSixYearsReview error:", e);
        throw e;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      apiErrorResponse,
      consultantBySlug,
      getConsultantBySlug,
      consultantById,
      deleteConsultant,
      consultantByIdGetter,
      consultantCreate,
      consultantUpdate,
      createConsultantStatus,
      createdConsultant,
      setConsultantById,
      setCreateConsultantStatus,
      setCreatedConsultant,
      editConsultantStatus,
      setEditConsultantStatus,
      deleteConsultantStatus,
      setDeleteConsultantStatus,
      displayConsultantStatus,
      setConsultantBySlug,
      setConsultantAccessRights,
      consultantAccessRights,
      consultantSixYearsReview,
      getConsultantSixYearsReview,
    }),
    [
      apiErrorResponse,
      consultantBySlug,
      getConsultantBySlug,
      consultantById,
      deleteConsultant,
      consultantByIdGetter,
      consultantCreate,
      consultantUpdate,
      createConsultantStatus,
      createdConsultant,
      setConsultantById,
      setCreateConsultantStatus,
      setCreatedConsultant,
      editConsultantStatus,
      setEditConsultantStatus,
      deleteConsultantStatus,
      setDeleteConsultantStatus,
      displayConsultantStatus,
      setConsultantAccessRights,
      consultantAccessRights,
      consultantSixYearsReview,
      getConsultantSixYearsReview,
    ]
  );
  return <ConsultantContext.Provider value={values} {...props} />;
};
